import React, { useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import 'babel-polyfill';
import SearchFilter from './searchfilter';
import SearchResults from './searchresults';
import { IProvider } from './interfaces';
import { } from 'googlemaps';
import { $setText, addQueryParam } from './util';
import Footer from './footer';
import { labels } from './labels';
import FormProgress from './formprogress';
import FadAlert from './fadalert';
import CustomInput from './custominput';

function getParam(s: string) {
    let params = new URLSearchParams(window.location.search);
    let p = params.get(s);
    if (p === 'undefined' || p === 'null') {
        return '';
    }
    return p;
}

function getAllParam(s: string) {
    let params = new URLSearchParams(window.location.search);
    let p = params.getAll(s);
    return p;
}

const Search = () => {
    const [providers, setProviders] = useState<IProvider[]>([]);
    const [filterOptions, setFilterOptions] = useState({ language: [], insurance: [], gender: [], clinicalInterest: [], insuranceMapping: {} });
    //const [markerCluster, setMarkerCluster] = useState<MarkerClusterer>();
    const [markers, setMarkers] = useState([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [totalProviders, setTotalProviders] = useState(0);    

    let lat = getParam('lat');
    let lng = getParam('lng');
    let place = getParam('place');
    let clinic = getParam('clinic');
    let npi = getParam('npi');
    let reason = getParam('reason');
    let insurance = getParam('insurance');
    let gender = getAllParam('gender');
    let language = getAllParam('language');
    let clinicalInterest = getAllParam('ci');
    let apptType = getParam('apptType') || "3";
    let providerName = getParam('name');
    let fad = getParam('fad');
    let radius = getParam('radius');
    let sort = getParam('sort');
    let age = getParam('age');
    let who = getParam('who');
    let zip = getParam('zip');
    
    // State for the fetched location data (latitude, longitude, place name, state)
    const [location, setLocation] = useState(null);
    // State for any errors during fetching or validation
    const [error, setError] = useState('');
    // State to indicate if data is currently being fetched
    const [loading, setLoading] = useState(false);

    function buildUrl(overrides: Record<string, any>) {
        if (sort === "d" && !place) {
            sort = null;
        }
        const params = {
            lat,
            lng,
            clinic,
            npi,
            place,
            insurance,
            name: providerName,
            apptType,
            radius,
            age,
            zip,
            sort,
            gender,
            language,
            ci: clinicalInterest,
            ...overrides
        };

        const queryParts: string[] = [];

        Object.entries(params).forEach(([key, value]) => {
            const parts = addQueryParam(key, value);
            for (let part of parts) {
                queryParts.push(part);
            }
        });

        return `${window.location.pathname}?${queryParts.join("&")}`;
    }


    useEffect(() => {
        const onPopState = () => {
            lat = getParam('lat');
            lng = getParam('lng');
            place = getParam('place');
            clinic = getParam('clinic');
            npi = getParam('npi');
            reason = getParam('reason');
            insurance = getParam('insurance');
            gender = getAllParam('gender');
            language = getAllParam('language');
            clinicalInterest = getAllParam('ci');
            apptType = getParam('apptType') || "3";
            providerName = getParam('name');
            fad = getParam('fad');
            radius = getParam('radius');
            sort = getParam('sort');
            age = getParam('age');
            who = getParam('who');
            zip = getParam('zip');
            getProviders(filterOptions.insuranceMapping);
        };

        window.addEventListener('popstate', onPopState);

        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, []);

    // Function to fetch coordinates based on the ZIP code
    const updateLocation = async (zip) => {
        // Basic validation: check if the ZIP code is 5 digits and consists only of numbers
        if (zip.length === 5 && /^\d+$/.test(zip)) {
            setLoading(true); // Start loading
            setError(''); // Clear previous errors
            setLocation(null); // Clear previous location data

            try {
                // Using Zippopotam.us API - a free alternative for this example.
                // A production Google Maps solution would typically require an API key.
                const response = await fetch(`https://api.zippopotam.us/us/${zip}`);

                if (!response.ok) {
                    // Handle HTTP errors (e.g., 404 Not Found, 500 Server Error)
                    if (response.status === 404) {
                        throw new Error('Invalid ZIP code. Please check and try again.');
                    }
                    throw new Error(`Network response was not ok. Status: ${response.status}`);
                }

                const data = await response.json();

                // Check if the API returned valid place data
                if (data.places && data.places.length > 0) {
                    const place = data.places[0];
                    setLocation({
                        latitude: place.latitude,
                        longitude: place.longitude,
                        placeName: place['place name'], // Accessing property with space
                        state: place.state,
                        country: data.country
                    });
                    lat = place.latitude;
                    lng = place.longitude;
                    history.pushState(null, "", buildUrl({}));
                    getProviders(filterOptions.insuranceMapping);
                } else {
                    // Handle cases where the ZIP code is valid format but not found by the API
                    setError('ZIP code not found or no location data available.');
                }
            } catch (e) {
                // Handle fetch errors or other exceptions
                setError(e.message || 'Failed to fetch coordinates. Please try again.');
                console.error("Fetch error:", e);
            } finally {
                setLoading(false); // Stop loading regardless of outcome
            }       
        } else {
            // If input is empty
            setError(''); // Clear error
            setLocation(null); // Clear location data
            history.pushState(null, "", buildUrl({zip:''}));
            getProviders(filterOptions.insuranceMapping);
        }
    };

    const handleZipChange = (result) => {
        lat = result.lat;
        lng = result.lng;
        place = result.name;
        
        history.pushState(null, "", buildUrl({}));
        getProviders(filterOptions.insuranceMapping);
        /*
        const newZip = event.target.value;
        // Allow only digits and limit length to 5
        if (/^\d*$/.test(newZip) && newZip.length <= 5) {
            zip = newZip
            // Automatically fetch if 5 digits are entered
            if (newZip.length === 5 || newZip.length == 0) {
                updateLocation(newZip);
            } else {
                // Clear location and error if not 5 digits
                setLocation(null);
                if (newZip.length > 0 && newZip.length < 5) {
                    setError('ZIP code must be 5 digits.');
                } else {
                    setError(''); // Clear error if input is empty or being corrected
                }
            }
        }*/
    };

    const onZipChange = (e) => {
        if (e.target.value === "") {
            lat = null;
            lng = null;
            place = null;
            
            (document.getElementById("sortBy") as HTMLSelectElement).value = "a";
            
            history.pushState(null, "", buildUrl({}));
            getProviders(filterOptions.insuranceMapping);
        }
    }

    function filterInsurance(providers: IProvider[], insuranceMapping) {
        for (let i = 0; i < providers.length; i++) {
            if (providers[i].insurance) {
                providers[i].insuranceList = providers[i].insurance.split("|");
            } else {
                providers[i].insuranceList = [];
            }
        }
        let selectedInsurance = getParam('insurance');
        if (selectedInsurance) {
            return providers.filter(p => p.insuranceList.map(i => insuranceMapping[i]).indexOf(selectedInsurance) >= 0);
        }
        return providers;
    }

    function filterGender(providers: IProvider[]) {
        let g = getAllParam('gender');
        if (g && g.length > 0) {
            let r = providers.filter(p => g.indexOf(p.gender) >= 0);
            return r;
        }
        return providers;
    }

    function filterLanguage(providers: IProvider[]) {
        let g = getAllParam('language');
        if (g && g.length > 0) {
            let r = [];
            for (let i = 0; i < providers.length; i++) {
                for (let j = 0; j < g.length; j++) {
                    if (providers[i].languageList.indexOf(g[j]) >= 0 && r.indexOf(providers[i]) < 0) {
                        r.push(providers[i]);
                    }
                }
            }
            return r;
        }
        return providers;
    }

    function filterClinicalInterest(providers: IProvider[]) {
        let g = getAllParam('ci');
        if (g && g.length > 0) {
            let r = [];
            for (let i = 0; i < providers.length; i++) {
                for (let j = 0; j < g.length; j++) {
                    if (providers[i].clinicalInterests.split("|").indexOf(g[j]) >= 0 && r.indexOf(providers[i]) < 0) {
                        r.push(providers[i]);
                    }
                }
            }
            return r;
        }
        return providers;
    }

    function filter(providers: IProvider[], insuranceMapping) {
        let p = filterInsurance(providers, insuranceMapping);
        p = filterGender(p);
        p = filterLanguage(p);
        p = filterClinicalInterest(p);
        return p;
    }

    function distance(lat1, lng1, lat2, lng2) {
        return Math.pow(lat1 - lat2, 2) + Math.pow(lng1 - lng2, 2);
    }


    function getProviders(insuranceMapping) {
        let query = 'api/providers';
        if (clinic) {
            query += `?clinic=${clinic}`
        } else if (npi) {
            query += `?npi=${npi}`
        } else if (lat && lng) {
            query += `?lat=${lat}&lng=${lng}`
        }
        if (getParam("apptType")) {
            query += ((query.indexOf("?") > 0) ? "&" : "?") + "apptType=" + getParam("apptType");
        }
        /*
        if (getParam("reason")) {
            query += ((query.indexOf("?") > 0) ? "&" : "?") + "reason=" + getParam("reason");
        }*/
        if (getParam("radius")) {
            query += ((query.indexOf("?") > 0) ? "&" : "?") + "radius=" + getParam("radius");
        }
        if (getParam("age")) {
            query += ((query.indexOf("?") > 0) ? "&" : "?") + "age=" + getParam("age");
        }
        /*
        if (getParam("who")) {
            query += ((query.indexOf("?") > 0) ? "&" : "?") + "who=" + getParam("who");
        }*/
        setIsLoading(true);
        axios.get(query)
            .then((result) => {
                setTotalProviders(result.data.length);
                preProcessProviders(result.data);
                let f = filter(result.data, insuranceMapping);

                if (place) {
                    const virtualists = [];
                    const inperson = [];

                    for (const obj of f) {
                        const { latitude, longitude } = obj.location;
                        if (latitude === 0 && longitude === 0) {
                            virtualists.push(obj);
                        } else {
                            inperson.push(obj);
                        }
                    }

                    if (lat && lng) {
                        if (sort === "d") {
                            /* Sort by distance */
                            inperson.sort((a, b) => {
                                const distA = distance(lat, lng, a.location.latitude, a.location.longitude);
                                const distB = distance(lat, lng, b.location.latitude, b.location.longitude);
                                return distA - distB;
                            })
                        }
                        for (let i = 0; i < inperson.length; i++) {
                            (inperson[i] as any).distance = distance(lat, lng, inperson[i].location.latitude, inperson[i].location.longitude);
                        }
                    }

                    f = [];
                    const MAX_DIST /* 20 miles */ = 0.101;
                    for (let i = 0; i < inperson.length; i++) {
                        if ((inperson[i] as any).distance < MAX_DIST) {
                            f.push(inperson[i])
                        }
                    }
                    for (let i = 0; i < virtualists.length; i++) {
                        f.push(virtualists[i])
                    }
                    for (let i = 0; i < inperson.length; i++) {
                        if ((inperson[i] as any).distance >= MAX_DIST) {
                            f.push(inperson[i])
                        }
                    }
                }
                setProviders(f);
                setIsLoading(false);
                var rad = getParam("radius");
                var zoomLevel = rad == "20" ? 10 : rad == "5" ? 12 : 11;
                if (document.documentElement.clientWidth < 576) {
                    zoomLevel -= 1;
                }
                const firstLocation = result.data.find(item => item.location.latitude)?.location;
                var mapOptions = {
                    zoom: zoomLevel,
                    center: lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } :
                        result.data.length > 0 && firstLocation ? { lat: parseFloat(firstLocation.latitude), lng: parseFloat(firstLocation.longitude) } :
                            { lat: 42.3601, lng: -71.0589 },
                    disableDefaultUI: true,
                    zoomControl: true
                    //styles: mapStyle,
                };
                const map = new google.maps.Map(document.getElementById('map'), mapOptions);

                let m = [];
                /*
                const markers = result.data.slice(0, 50).filter(p => p.location && p.location.latitude)
                    .map(p => { return new google.maps.Marker({ position: { lat: p.location.latitude, lng: p.location.longitude } }) });
                map.set
                setMarkerCluster(new MarkerClusterer({ map, markers }));
                */
                for (let i = 0; i < f.length; i++) {
                    let p = f[i];
                    if (p.location && p.location.latitude && p.location.longitude) {
                        var marker = new google.maps.Marker({
                            position: { lat: p.location.latitude, lng: p.location.longitude },
                            map: map
                        });
                        m.push(marker);
                    }
                }
                setMarkers(m);
            })
    }

    useEffect(() => {
        axios.get('api/filterOptions')
            .then((result) => {
                setFilterOptions(result.data);
                getProviders(result.data.insuranceMapping);
            });
    }, []);

    function preProcessProviders(providers: IProvider[]) {
        if (providers) {
            for (let i = 0; i < providers.length; i++) {
                if (providers[i].language) {
                    providers[i].languageList = providers[i].language.split("|");
                } else {
                    providers[i].languageList = [];
                }
                if (providers[i].languageList.indexOf("English") < 0) {
                    providers[i].languageList = ['English', ...providers[i].languageList];
                }
            }
        }
    }

    function insuranceChange(e: React.ChangeEvent<HTMLSelectElement>) {
        history.pushState(null, "", buildUrl({ insurance: e.currentTarget.value }));            
        getProviders(filterOptions.insuranceMapping);
    }

    var info: google.maps.InfoWindow;

    function rowEnter(p: IProvider) {
        if (!markers) { return; }
        for (let j = 0; j < markers.length; j++) {
            if (markers[j].getPosition().lat() === p.location.latitude && markers[j].getPosition().lng() === p.location.longitude) {
                info = new google.maps.InfoWindow({
                    content: `<div>${p.firstName} ${p.lastName}<br />${p.address1}<br/>` +
                        (p.address2 ? (p.address2 + "<br />") : "") +
                        `${p.city}, ${p.state} ${p.zipCode}</div>`
                })
                info.open({
                    anchor: markers[j],
                    shouldFocus: false
                })
                return;
            }
        }
    }

    function rowLeave() {
        if (info) {
            info.close();
        }
    }

    function getCheckboxValues(id: string) {
        let cb = Array.from(document.getElementById(id).getElementsByTagName("input"));
        return cb.filter(c => (c as HTMLInputElement).checked).map(c => c.value)
    }

    function applyFilters() {
        setShowFilters(false);
        
        history.pushState(null, "", buildUrl({
            gender: getCheckboxValues("filterGender"),
            language: getCheckboxValues("filterLanguage"),
            ci: getCheckboxValues("filterClinicalInterest")
        }));
        getProviders(filterOptions.insuranceMapping);
    }

    function selectApptType(e: React.ChangeEvent<HTMLInputElement>) {
        apptType = e.currentTarget.value;
        history.pushState(null, "", buildUrl({ apptType }));
        getProviders(filterOptions.insuranceMapping);
    }

    function selectRadius(e: React.ChangeEvent<HTMLSelectElement>) {
        history.pushState(null, "", buildUrl({ "radius": e.currentTarget.value }));
        getProviders(filterOptions.insuranceMapping);
    }

    function selectSort(e: React.ChangeEvent<HTMLSelectElement>) {
        sort = e.currentTarget.value;
        history.pushState(null, "", buildUrl({ sort }));
        getProviders(filterOptions.insuranceMapping);
    }

    function getResultString(numResults: number, providerPractice: string, place: string) {
        if (providerPractice) {
            if (numResults === 1) {
                return labels.search.providerPracticeResult.replace("${providerPractice}", providerPractice.substring(0, 100));
            } else {
                return labels.search.providerPracticeResults.replace("${numResults}", "" + numResults)
                    .replace("${providerPractice}", providerPractice.substring(0, 100));
            }
        } else {
            if (numResults === 1) {
                return labels.search.locationResult.replace("${place}", place.replace(", USA", "").substring(0, 100)).replace("${miles}", getParam("radius") || "10");
            } else {
                return labels.search.locationResults.replace("${numResults}", "" + numResults)
                    .replace("${place}", place.replace(", USA", "").substring(0, 100)).replace("${miles}", getParam("radius") || "10");
            }
        }
    }

    return (<>
        <div className="container bg-white px-4 px-sm-5">
            {/* 
            <div className="text-center pt-4 pb-4">
                <a href="./"><img className="d-block mx-auto" src="img/beth-israel-lahey-health-logo-web-crop2.png" alt="Beth Israel Lahey Health" style={{ maxWidth: "320px" }} /></a>
            </div>
            */}
            <div className="pt-4 row">
                <div className="col-lg-4">
                    <a href="./"><img className="d-block" src="img/beth-israel-lahey-health-logo-web-crop2.png" alt="Beth Israel Lahey Health" style={{ maxWidth: "320px" }} /></a>
                </div>
                <div className="col-lg-8" style={{ marginTop: "18px" }}>
                    <FormProgress currentStep={1} />
                </div>
            </div>
            {fad &&
                <div className="alert alert-info mb-4 text-center" role="alert">
                    {providers.length > 0 && labels.search.fad(providers[0].ageCutoff)}
                </div>
            }
            {!fad && npi && providers.length > 0 && parseInt(age) < providers[0].ageCutoff &&
                <div className="alert alert-info mb-4" role="alert">
                    {labels.search.ageCutoff(providers[0].ageCutoff)}
                </div>}
           
            <div className="row mb-2 bg-white">
                <div>
                    {totalProviders >= 0 &&
                        <div className="filter-row row pt-1 mb-2 mr-1 ml-3">
                            {!npi && <>
                                <div className="mr-4 mb-2">
                                    <div className="mb-2" id="vistTypeGroupLabel">{labels.search.appointmentType}</div>

                                    <div className="btn-group mr-2" style={{ marginBottom: "-0.5rem" }} role="radiogroup" aria-labelledby="vistTypeGroupLabel">
                                        <input type="radio" className="btn-check" name="visitType" id="visitTypeAll" autoComplete="off" defaultChecked={apptType === "3"} onChange={selectApptType} value="3" />
                                        <label className="btn btn-outline-primary" htmlFor="visitTypeAll">All</label>
                                        <input type="radio" className="btn-check" name="visitType" id="visitTypeInPerson" autoComplete="off" defaultChecked={!apptType || apptType === "1"} onChange={selectApptType} value="1" />
                                        <label className="btn btn-outline-primary" htmlFor="visitTypeInPerson">{labels.search.inPerson}</label>
                                        <input type="radio" className="btn-check" name="visitType" id="visitTypeVirtual" autoComplete="off" defaultChecked={apptType === "2"} onChange={selectApptType} value="2" />
                                        <label className="btn btn-outline-primary" htmlFor="visitTypeVirtual">{labels.search.virtualVisit}</label>                                    
                                    </div>
                                </div>
                            
                                <div className="mr-4 mb-2">
                                    <div><label className="input-row-label mr-2" htmlFor="visitDropdown">Insurance</label></div>
                                    <div>
                                        <select className="custom-select" id="visitDropdown" onChange={insuranceChange} style={{ width: "250px", borderColor: "var(--primary-color)" }}>
                                        <option selected value="">{labels.search.choose}...</option>
                                        {filterOptions.insurance && filterOptions.insurance.map((i) => (
                                            <option value={i.value} selected={i.value == insurance}>{i.name}</option>
                                        ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="mr-4 mb-2">
                                    <div><label className="input-row-label mr-2" htmlFor="visitDropdown">Zip Code / City</label></div>
                                    <div>
                                        <CustomInput inputId="zipcity" placeholder="Zip Code or City" defaultValue={place} style={{ width: "200px" }}
                                            disabled={apptType === "2" || clinic != null}
                                            onChange={onZipChange}
                                            locationChanged={handleZipChange}
                                            autoComplete="off" maxLength={50} />                                        
                                    </div>
                                </div>
                                
                                <div className="mr-4 mb-2">
                                    <div><label className="input-row-label mr-2" htmlFor="visitDropdown">Sort By</label></div>
                                    <div>
                                        <select className='custom-select' style={{ paddingRight: "30px", borderColor: "var(--primary-color)" }} id="sortBy" onChange={selectSort} defaultValue={sort || "a"} disabled={!place || apptType === "2"}><option value="a">Availability</option><option value="d">Distance</option></select>
                                    </div>
                                </div>

                                {false && lat && lng &&
                                    <div className="form-inline mr-4 mb-2">
                                        <label className="input-row-label mr-2" htmlFor="visitDropdown">Search Radius</label>
                                        <select className='custom-select' style={{ paddingRight: "30px" }} id="searchRadius" onChange={selectRadius} defaultValue={radius || "20"}><option value="5">5 miles</option><option value="10">10 miles</option><option value="20">20 miles</option></select>
                                    </div>
                                }
                                <div className="">
                                    <label className="input-row-label">&nbsp;</label>
                                    <button type="button" id="moreFilters" className="btn btn-primary mb-2" data-toggle="modal" data-target="#moreFilters" onClick={() => setShowFilters(true)}>
                                        {labels.search.moreFilters}
                                    </button>
                                </div>
                                
                                <SearchFilter show={showFilters} options={filterOptions} onClose={applyFilters} gender={gender} language={language} clinicalInterest={clinicalInterest} />
                            </>}
                        </div>
                    }
                </div>
            </div>
            <div className="row">
                <div className="col-lg-7 col-xl-8 col-xl-75">
                    {providers.length === 1 && providers[0].npi === "__PRACTICE__"
                        ? <div>Please call clinic office at <a href={"tel:" + providers[0].phone}>{providers[0].phone}</a> to speak with the clinic staff to schedule an appointment.</div>
                        : <SearchResults totalProviders={totalProviders} providers={providers} rowEnter={rowEnter} rowLeave={rowLeave} age={age} lat={lat} lng={lng} place={place} isLoading={isLoading} reason={reason} apptType={apptType} who={who} backto={encodeURIComponent(window.location.search)} />
                    }
                </div>
                {(apptType !== "2" && (providers.filter(p => !p.virtualist).length > 0)) &&
                    <div className="col-lg-5 col-xl-4 col-xl-45">
                        <div className="sticky-top">
                            <div id="locationInfo" style={{ position: 'absolute', background: 'white' }}>{location && (zip + ' ' + location.placeName)}</div>
                            <div id="map"></div>
                        </div>
                    </div>}
            </div>

        </div>
        {false && fad && age && parseInt(age) > 0 && <FadAlert age={age} />}
        <Footer />
    </>);
}

export default Search;